import {ComponentStore} from '@ngrx/component-store';
import {Injectable} from '@angular/core';
import {Actions, ofType} from "@ngrx/effects";
import {ReportStoreState} from "./report";
import {
  createSavedReportsSuccess,
  updateSavedReportsSuccess
} from "../savedReports/savedReports.actions";
import {filter, first, map, skip, switchMap, tap} from 'rxjs/operators';
import {ActivatedRoute, Data, Params, Router} from '@angular/router';
import {TypedAction} from "@ngrx/store/src/models";
import {Serializer, SerializerResponse, Serializers} from '../../interfaces/serializer';
import {SavedReport} from "../../interfaces/saved-reports";
import {Observable, Subscription, withLatestFrom, combineLatest, of} from 'rxjs';
import {ReportsRequestsService} from "../../services/reports-requests.service";
import {TranslateService} from "@ngx-translate/core";
import {AdReportParams, AdRequestOptions, AdRequestParams, AdRPeriod, AdRReportType, ReportTemplate} from '../../interfaces/ad-reports';
import {createExportsSuccess} from "../exports/exports.actions";
import {initSite} from "../init/init.actions";
import {LocalStorageService} from "../../services/local-storage.service";
import {AppState} from "../store";
import {Store} from '@ngrx/store';
import {AdRRequestParams} from "../../classes/ad-r-request-params";
import {PeriodsService} from "../../services/periods.service";
import {
  reportDownload,
  reportExit,
  reportOpenDownloadDialog,
  reportOpenExitDialog,
  reportOpenNewLinkDialog,
  reportOpenSaveDialog,
  reportOpenSendDialog,
  reportOpenShareWithLinkDialog,
  reportUpdateSettings
} from './report.actions';
import {dialogOpen} from "../dialog/dialog.actions";
import {ReportSaveDialogComponent} from "../../components/report/report-save-dialog/report-save-dialog.component";
import {
  ReportSendDialogComponent
} from "../../components/report/report-send-dialog/report-send-dialog.component";
import {DialogConfirmComponent} from "../../components/dialog/dialog-confirm/dialog-confirm.component";
import {Location} from '@angular/common';
import {Site} from "../../interfaces/site";
import {selectSite, selectSiteID} from '../init/init.selectors';
import {DialogLinkInfoComponent} from "../../components/dialog/dialog-link-info/dialog-link-info.component";
import {
  ReportSharePublicDialogComponent
} from "../../components/report/report-share-public-dialog/report-share-public-dialog.component";
import {environment} from "../../../../environments/environment";
import {ExitReportGuardService} from '../../services/exit-report-guard.service';
import {selectSavedReportByID, selectSavedReportsInitialized, selectSavedReportsLoaded} from '../savedReports/savedReports.selectors';
import {ParamsConfigsService} from '../../services/params-configs.service';
import {ParamConfig} from '../../interfaces/params';
import {ParamFormatters, ReportParamsService} from '../../services/report-params.service';
import {selectTemplateByID} from '../templates/templates.selectors';
import {
  disableMetricsConsentAutoCorrection,
  disableMetricsConsentAutoCorrectionSuccess,
  enableMetricsConsentAutoCorrection,
  enableMetricsConsentAutoCorrectionSuccess
} from '../metricsConsentAutoCorrection/metricsConsentAutoCorrection.actions';
import {
  selectBackgroundLoaded,
  selectDimensionComponentObjects,
  selectMetricComponentObjects
} from '../selectors';
import {selectChannelsNotRequired} from '../channels/channels.selectors';
import {DimensionComponentObject, DimensionComponentObjects} from '../../classes/dimension-component-object';
import {MetricComponentObject, MetricComponentObjects} from '../../classes/metric-component-object';
import {Channel} from '../../interfaces/channel';
import {selectPeriods} from '../periods/periods.selectors';
import {SavedReportComponentObject} from "../../classes/saved-report-component-object";
import * as lodash from 'lodash';
import {UserComponentObject} from "../../classes/user-component-object";
import {changeRealConversionsCorrectionModeSuccess} from "../profile/profile.actions";
import {SavedReportsService} from "../../services/saved-reports.service";

@Injectable({
  providedIn: 'root'
})
export class ReportStore extends ComponentStore<ReportStoreState> {
  private readonly _dataSet$: Observable<Serializer<Site>> = this._store.select(selectSite);
  private readonly _siteID$: Observable<number> = this._store.select(selectSiteID);

  public readonly state$: Observable<ReportStoreState> = this.select((state: ReportStoreState): ReportStoreState => state);
  public readonly disabled$: Observable<boolean> = this.select((state: ReportStoreState): boolean => state.disabled);
  public readonly loading$: Observable<boolean> = this.select((state: ReportStoreState): boolean => state.loading);
  public readonly initialized$: Observable<boolean> = this.select((state: ReportStoreState): boolean => state.initialized);
  public readonly submitted$: Observable<boolean> = this.select((state: ReportStoreState): boolean => state.submitted);
  public readonly authorize$: Observable<boolean> = this.select((state: ReportStoreState): boolean => state.authorize);

  public readonly report$: Observable<Serializer<SavedReport> | null> = this.select((state: ReportStoreState): Serializer<SavedReport> | null => state.report);
  public readonly template$: Observable<Serializer<ReportTemplate> | null> = this.select((state: ReportStoreState): Serializer<ReportTemplate> | null => state.template);
  public readonly type$: Observable<'report' | 'template' | 'url' | 'local' | 'public' | null> = this.select((state: ReportStoreState): 'report' | 'template' | 'url' | 'local' | 'public' | null => state.type);
  public readonly reportType$: Observable<AdRReportType> = this.select((state: ReportStoreState): AdRReportType => state.reportType);
  public readonly limit$: Observable<number> = this.select((state: ReportStoreState): number => state.limit);
  public readonly name$: Observable<string> = this.select((state: ReportStoreState): string => state.name);
  public readonly owner$: Observable<string> = this.select((state: ReportStoreState): string => state.owner);

  public readonly publicMode$: Observable<boolean> = this.type$.pipe(
    map((type: 'report' | 'template' | 'url' | 'local' | 'public' | null): boolean => type === 'public')
  );

  public readonly requestParams$: Observable<Partial<AdRequestParams> | null> = this.select((state: ReportStoreState): Partial<AdRequestParams> | null => state.requestParams);
  public readonly requestParamsSubmitted$: Observable<Partial<AdRequestParams> | null> = this.select((state: ReportStoreState): Partial<AdRequestParams> | null => state.requestParamsSubmitted);

  public readonly reportParams$: Observable<Partial<AdReportParams> | null> = this.select((state: ReportStoreState): Partial<AdReportParams> | null => state.reportParams);
  public readonly reportParamsSubmitted$: Observable<Partial<AdReportParams> | null> = this.select((state: ReportStoreState): Partial<AdReportParams> | null => state.reportParamsSubmitted);

  public readonly requestOptions$: Observable<Partial<AdRequestOptions>> = this.select((state: ReportStoreState): Partial<AdRequestOptions> => state.requestOptions);

  public readonly paramsConfigs$: Observable<Array<ParamConfig>> = this.select((state: ReportStoreState): Array<ParamConfig> => state.paramsConfigs);
  public readonly actionsConfigs$: Observable<Array<ParamConfig>> = this.select((state: ReportStoreState): Array<ParamConfig> => state.actionsConfigs);
  public readonly paramsDropdownConfigs$: Observable<Array<ParamConfig>> = this.select((state: ReportStoreState): Array<ParamConfig> => state.paramsDropdownConfigs);
  public readonly helpTemplate$: Observable<string> = this.select((state: ReportStoreState): string => state.helpTemplate);
  public readonly disabledValues$: Observable<{ [controlName: string]: Array<any> }> = this.select((state: ReportStoreState) => state.disabledValues);
  public readonly isPublic$: Observable<boolean> = this.select((state: ReportStoreState): boolean => state.report?.attributes?.is_public || false);
  public readonly publicLink$: Observable<string | undefined> = this.select((state: ReportStoreState): string | undefined => state.report && `${environment.baseUrl}/public-reports/password/${state.report.attributes.uuid}` || undefined);

  // #############################
  // EVENTS
  // #############################
  public readonly requestOptionsChanges$: Observable<Partial<AdRequestOptions>> = this.requestOptions$
    .pipe(
      withLatestFrom(this.state$),
      filter(([options, state]: [Partial<AdRequestOptions>, ReportStoreState]): boolean => state.initialized),
      skip(1),
      map(([options, state]: [Partial<AdRequestOptions>, ReportStoreState]): Partial<AdRequestOptions> => options)
    );

  private readonly _initSavedReport: () => void = this.effect((params$: Observable<void>): Observable<[SavedReportComponentObject, ReportStoreState, number]> => params$.pipe(
    tap((): void => {
      this.patchState({
        type: 'report',
        loading: true,
        initialized: false
      });
    }),
    switchMap((): Observable<boolean> => this._store.select(selectSavedReportsInitialized)),
    filter((loaded: boolean): boolean => loaded),
    switchMap((): Observable<SavedReportComponentObject | undefined> => this._store.select(selectSavedReportByID(this._route.snapshot.params.report_id))),
    switchMap((report: SavedReportComponentObject): Observable<SavedReportComponentObject> => {
      if (report.parameters$.value) {
        return of(report);
      }

      return this._savedReportService.getSavedReport(report.payload.id).pipe(
        map((savedReport: SerializerResponse<Serializer<SavedReport>>): SavedReportComponentObject => {
          report.setParameters(savedReport.data.attributes.parameters);
          return report;
        }));
    }),
    first(),
    withLatestFrom(this.state$, this._siteID$),
    tap(([report, state, siteID]: [SavedReportComponentObject, ReportStoreState, number]): void => {
      const savedReport: Serializer<SavedReport> = report && lodash.cloneDeep(report.payload) || undefined;

      if (savedReport === undefined) {
        //redirect to saved reports page
        this._router.navigate([siteID, 'saved-reports']).then();
      } else if (!savedReport.attributes.authorize) {
        this.patchState({
          authorize: false,
          loading: false,
          report: report.payload,
          name: savedReport.attributes.name,
          owner: report.payload.relationships.user.data.attributes.email
        });
      } else {
        if (state.iframe) {
          if (savedReport.attributes.report_type === 'dashboard') {
            savedReport.attributes.parameters.period = { ...savedReport.attributes.parameters.period, ...state.periodFromUrl };
            savedReport.attributes.parameters.dashboard.forEach(dash => {
              if (dash.config.data === undefined) {
                dash.config.filters = { ...dash.config.filters, ...state.filtersFromUrl };
              }

              dash.config.data?.forEach(dashData => {
                dashData.filters = { ...dashData.filters, ...state.filtersFromUrl };
              });
            });
          } else {
            savedReport.attributes.parameters.filters = { ...savedReport.attributes.parameters.filters, ...state.filtersFromUrl };
            savedReport.attributes.parameters.period = { ...savedReport.attributes.parameters.period, ...state.filtersFromUrl };
          }
        }

        this._getPeriodFromUrl(savedReport.attributes.parameters);
        this._getUrlParameters(savedReport);

        this.patchState({
          report: savedReport,
          name: savedReport.attributes.name,
          reportType: savedReport.attributes.report_type,
          requestParams: savedReport.attributes.parameters,
          limit: savedReport.attributes.parameters.limit,
          loading: false
        });
      }
    })
  ));

  private readonly _initTemplate: () => void = this.effect((params$: Observable<void>): Observable<Serializer<ReportTemplate>> => params$.pipe(
    tap((): void => {
      this.patchState({
        type: 'template',
        loading: true,
        initialized: false
      });
    }),
    switchMap((): Observable<Serializer<ReportTemplate>> => this._store.select(selectTemplateByID(this._route.snapshot.params.template_id))),
    filter((template: Serializer<ReportTemplate> | undefined): boolean => template !== undefined),
    tap((template: Serializer<ReportTemplate>): void => {
      this._getPeriodFromUrl(template.attributes.parameters);
      this._getUrlParameters(template);

      this.patchState({
        template: template,
        name: template.attributes.name,
        requestParams: template.attributes.parameters,
        reportType: template.attributes.options.type,
        loading: false
      });
    })
  ));

  private readonly _init: () => void = this.effect((params$: Observable<void>): Observable<[void, ReportStoreState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([_, state]: [void, ReportStoreState]): void => {
      const urlReportParams: Partial<AdRequestParams> | null = this._getUrlParameters();
      const initReportParams: Partial<AdRequestParams> = {};

      for (const param of [...state.paramsConfigs, ...state.paramsDropdownConfigs, ...state.actionsConfigs]) {
        if ('controlName' in param) {
          initReportParams[param.controlName] = param.initValue;
        }
      }

      this.patchState({
        requestParams: {
          ...initReportParams,
          ...urlReportParams
        },
        requestOptions: {},
        type: urlReportParams && 'url' || null
      });
    })
  ));

  private readonly _initLocalStorage: (params: { key: string }) => void = this.effect((params$: Observable<{ key: string }>): Observable<Partial<AdRequestParams>> => params$.pipe(
    tap((): void => {
      this.patchState({
        type: 'local'
      });
    }),
    withLatestFrom(this.state$),
    switchMap(([params, state]: [{ key: string }, ReportStoreState]) => this._siteID$.pipe(
      switchMap((datasetId: number): Observable<Partial<AdRequestParams>> => this._localStorageS.get(`${params.key}-${datasetId}`).pipe(
        tap((data: Partial<AdRequestParams>): void => {
          if (data) {
            this.patchState({
              requestParams: data
            });
          } else {
            const initReportParams: Partial<AdRequestParams> = {};

            for (const param of [...state.paramsConfigs, ...state.paramsDropdownConfigs, ...state.actionsConfigs]) {
              if ('controlName' in param) {
                initReportParams[param.controlName] = param.initValue;
              }
            }

            this.patchState({
              requestParams: initReportParams
            });
          }

          this._initReportParams();
        })
      ))
    ))
  ));

  private readonly _saveLocalStorage: (params: { key: string }) => void = this.effect((params$: Observable<{ key: string }>): Observable<boolean> => params$.pipe(
    withLatestFrom(this.state$, this._siteID$),
    switchMap(([params, state, datasetId]: [{ key: string }, ReportStoreState, number]): Observable<boolean> => this._localStorageS.set(`${params.key}-${datasetId}`, state.requestParams))
  ));

  private readonly _initParams: (params: {
    initParams: Partial<AdRequestParams>,
    requestParams: Partial<AdRequestParams>,
    reportParams: Partial<AdReportParams>,
    requestOptions: Partial<AdRequestOptions>,
    uuid: string | null,
    token: string | null,
    limit: number | null,
    report: Serializer<SavedReport> | null
  }) => void = this.effect((params$: Observable<{
    initParams: Partial<AdRequestParams>,
    requestParams: Partial<AdRequestParams>,
    reportParams: Partial<AdReportParams>,
    requestOptions: Partial<AdRequestOptions>,
    uuid: string | null,
    token: string | null,
    limit: number | null,
    report: Serializer<SavedReport> | null
  }>): Observable<{
    initParams: Partial<AdRequestParams>,
    requestParams: Partial<AdRequestParams>,
    reportParams: Partial<AdReportParams>,
    requestOptions: Partial<AdRequestOptions>,
    uuid: string | null,
    token: string | null,
    limit: number | null,
    report: Serializer<SavedReport> | null
  }> => params$.pipe(
    tap((params: {
      initParams: Partial<AdRequestParams>,
      requestParams: Partial<AdRequestParams>,
      reportParams: Partial<AdReportParams>,
      requestOptions: Partial<AdRequestOptions>,
      uuid: string | null,
      token: string | null,
      limit: number | null,
      report: Serializer<SavedReport> | null
    }): void => {
      this.patchState({
        initialized: true,
        submitted: true,
        uuid: params.uuid,
        token: params.token,
        requestParams: params.requestParams,
        requestParamsSubmitted: params.requestParams,
        reportParams: params.reportParams,
        reportParamsSubmitted: params.reportParams,
        requestOptions: params.requestOptions,
        limit: params.limit,
        type: null,
        report: params.report
      });
    })
  ));

  private readonly _routeParamsChanges: () => void = this.effect((params$: Observable<void>): Observable<[void, Params, ReportStoreState]> => params$.pipe(
    withLatestFrom(this._route.params, this.state$),
    filter(([_, params, state]: [void, Params, ReportStoreState]) => !state.initialized),
    tap(([_, params]: [void, Params, ReportStoreState]): void => {
      if ('report_id' in params) {
        this._initSavedReport();
      } else if ('template_id' in params) {
        this._initTemplate();
      } else {
        this._init();
      }
    })
  ));

  private readonly _routeDataChanges: () => void = this.effect((params$: Observable<void>): Observable<Data> => params$.pipe(
    switchMap(() => this._route.data.pipe(
      filter((data: Data): boolean => 'reportType' in data),
      withLatestFrom(this.state$),
      tap(([data, state]: [Data, ReportStoreState]): void => {
        this.patchState({
          reportType: data.reportType
        });

        if (data.reportType in state.parameters) {
          this.patchState({
            paramsConfigs: this._paramsConfigS.getConfig(state.parameters[data.reportType].paramsConfig),
            actionsConfigs: this._paramsConfigS.getConfig(state.parameters[data.reportType].actionsConfig),
            paramsDropdownConfigs: this._paramsConfigS.getConfig(state.parameters[data.reportType].paramsDropdownConfig),
            helpTemplate: state.parameters[data.reportType].helpTemplate
          });
        }
      })
    ))
  ));

  private readonly _routeQueryChanges: () => void = this.effect((params$: Observable<void>): Observable<Params> => params$.pipe(
    switchMap(() => this._route.queryParams.pipe(
      withLatestFrom(this.state$),
      tap(([params, state]: [Params, ReportStoreState]): void => {
        if (state.iframe) {
          const paramsObj = {};

          for (const queryParam in params) {
            if (!['from','to','auth_token'].includes(queryParam)) {
              paramsObj[queryParam] = params[queryParam].split('|||');
            } else {
              if (queryParam != 'auth_token') {
                if (params['from'] !== undefined && params['to'] !== undefined) {
                  this.patchState({
                    periodFromUrl: { type: 'custom', from: params['from'], to: params['to'] }
                  });
                }
              }
            }
          }

          this.patchState({
            filtersFromUrl: paramsObj
          });
        }
      }))
    )
  ));

  private readonly _createExportsSuccess: () => void = this.effect((params$: Observable<void>) => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(createExportsSuccess),
      withLatestFrom(this._dataSet$, this.state$),
      switchMap(([_, dataSet, state]) => this._reportsRequestsS.getSavedReportInfos(dataSet, state.uuid, this._route.snapshot.params.report_id).pipe(
        tap((report: SerializerResponse<Serializer<SavedReport>>): void => {
          this.patchState({
            report: report.data
          });
        })
      ))
    ))
  ));

  private readonly _createReportSuccess: () => void = this.effect((params$: Observable<void>): Observable<[TypedAction<string> & { report: Serializer<SavedReport>, exit: boolean }, ReportStoreState, number]> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(createSavedReportsSuccess),
      withLatestFrom(this.state$, this._siteID$),
      tap(([action, state, siteID]: [TypedAction<string> & { report: Serializer<SavedReport>, exit: boolean }, ReportStoreState, number]): void => {
        if (!action.exit) {
          // update url
          this._location.go(`/${siteID}/ad-reports/${state.reportType}/saved/${action.report.id}`);

          if (action.report.attributes.is_public) {
            this._store.dispatch(reportOpenNewLinkDialog({ link: `${environment.baseUrl}/reports/${action.report.attributes.uuid}` }));
          }
        }

        this.patchState({
          report: action.report,
          name: action.report.attributes.name,
        });
      })
    ))
  ));

  private readonly _createUpdateReportSuccess: () => void = this.effect((params$: Observable<void>): Observable<TypedAction<string> & Partial<{ report: Serializer<SavedReport>, exit: boolean, link: string }>> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(createSavedReportsSuccess, updateSavedReportsSuccess),
      tap((action: TypedAction<string> & Partial<{ report: Serializer<SavedReport>, exit: boolean, link: string }>): void => {
        if (action.exit) {
          //exit report
          this.exit();
          this._store.dispatch(reportExit());
        }

        this.patchState({
          report: action.report,
          name: action.report.attributes.name,
        });
      })
    ))
  ));

  private readonly _updateSite: () => void = this.effect((params$: Observable<void>): Observable<TypedAction<string> & { site: Serializer<Site> }> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(initSite),
      tap((action: TypedAction<string> & { site: Serializer<Site> }): void => {
        this._router.navigate([`/${action.site.id}/dashboard`]);
      })
    ))
  ));

  private readonly _updateRequestParams: (params: Partial<AdRequestParams>) => void = this.effect((params$: Observable<Partial<AdRequestParams>>): Observable<[Partial<AdRequestParams>, ReportStoreState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([params, state]: [Partial<AdRequestParams>, ReportStoreState]): void => {
      this.patchState({
        requestParams: {
          ...state.requestParams,
          ...params
        }
      });
    })
  ));

  private readonly _updateRequestParamsSubmitted: (params: Partial<AdRequestParams>) => void = this.effect((params$: Observable<Partial<AdRequestParams>>): Observable<[Partial<AdRequestParams>, ReportStoreState]> => params$.pipe(
    withLatestFrom(this.state$),
    filter(([_, state]: [Partial<AdRequestParams>, ReportStoreState]): boolean => state.initialized),
    tap(([params, state]: [Partial<AdRequestParams>, ReportStoreState]): void => {
      const requestParams: Partial<AdRequestParams> = {
        ...state.requestParams,
        ...params
      };

      const requestParamsSubmitted: Partial<AdRequestParams> = {
        ...state.requestParamsSubmitted,
        ...params
      };

      this.patchState({
        requestParams: requestParams,
        requestParamsSubmitted: requestParamsSubmitted
      });
    })
  ));

  private readonly _updateReportParams: (params: Partial<AdRRequestParams>) => void = this.effect((params$: Observable<Partial<AdRRequestParams>>): Observable<[Partial<AdRRequestParams>, ReportStoreState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([params, state]: [Partial<AdRRequestParams>, ReportStoreState]): void => {
      const requestParams: Partial<AdRequestParams> = state.requestParams || {};

      for (const key in params) {
        const value: any | null = this._reportParamsService.format(key as keyof ParamFormatters, params[key]);

        if (value !== null) {
          requestParams[key] = value;
        }
      }

      this.patchState({
        requestParams: requestParams,
        requestParamsSubmitted: requestParams,
        reportParams: {
          ...state.reportParams,
          ...params
        }
      });
    })
  ));

  private readonly _updateReportParamsSubmitted: (params: Partial<AdReportParams>) => void = this.effect((params$: Observable<Partial<AdReportParams>>): Observable<[Partial<AdRRequestParams>, ReportStoreState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([params, state]: [Partial<AdReportParams>, ReportStoreState]): void => {
      const requestParams: Partial<AdRequestParams> = state.requestParams || {};
      const reportParams: Partial<AdReportParams> = {
        ...state.reportParams,
        ...params
      };

      for (const key in params) {
        const value: any | null = this._reportParamsService.format(key as keyof ParamFormatters, params[key]);

        if (value !== null) {
          requestParams[key] = value;
        }
      }

      this.patchState({
        submitted: true,
        reportParams: reportParams,
        reportParamsSubmitted: reportParams,
        requestParams: requestParams,
        requestParamsSubmitted: requestParams,
      });
    })
  ));

  private _setDisabledValues: (params: {controlName: string, options: any}) => void = this.effect((params$: Observable<{controlName: string, options: any}>): Observable<[{controlName: string, options: any}, ReportStoreState]> => params$.pipe(
    withLatestFrom(this.state$),
    tap(([params, state]): void => {
      this.patchState({
        disabledValues: {
          ...state.disabledValues,
          [params.controlName]: params.options
        }
      });
    })
  ));

  public addFilter: (params: {slug: string, value: string}) => void = this.effect((params$: Observable<{slug: string, value: string}>) => params$.pipe(
    withLatestFrom(this.state$),
    tap(([params, state]: [{slug: string, value: string}, ReportStoreState]): void => {
      const filters: any = {
        ...state.requestParams.filters,
        [params.slug]: state.requestParams.filters[params.slug] ? [...state.requestParams.filters[params.slug], params.value] : [params.value]
      };

      filters[params.slug] = filters[params.slug].sort((a, b) => a.localeCompare(b));

      this.patchState({
        requestParams: {
          ...state.requestParams,
          filters: filters
        }
      });
    }
  )));

  public removeFilter: (params: {slug: string, value: string}) => void = this.effect((params$: Observable<{slug: string, value: string}>) => params$.pipe(
    withLatestFrom(this.state$),
    tap(([params, state]): void => {
      const filters = {
        ...state.requestParams.filters,
        [params.slug]: state.requestParams.filters[params.slug].filter((val: string): boolean => val !== params.value)
      };

      if (filters[params.slug].length === 0) {
        delete filters[params.slug];
      }

      this.patchState({
        requestParams: {
          ...state.requestParams,
          filters: filters
        }
      });
    }
  )));

  public hasFilter(slug: string, value: string): Observable<boolean> {
    return this.select((state: ReportStoreState): boolean => state.requestParams?.filters?.[slug]?.includes(value) || false);
  }

  private readonly _settings$: Subscription = this.effect((): Observable<[TypedAction<string> & { settings: any }, ReportStoreState]> => this._actions$.pipe(
    ofType(reportUpdateSettings),
    withLatestFrom(this.state$),
    tap(([action, state]: [TypedAction<string> & { settings: any }, ReportStoreState]): void => {
      this.patchState({
        reportParams: {
          ...state.reportParams,
          ...action.settings
        },
        reportParamsSubmitted: {
          ...state.reportParams,
          ...action.settings
        },
        requestParams: {
          ...state.requestParams,
          ...action.settings
        },
        requestParamsSubmitted: {
          ...state.requestParams,
          ...action.settings
        }
      });
    })
  ));

  // #############################
  // DIALOGS
  // #############################

  private readonly _newLinkDialog: () => void = this.effect((params$: Observable<void>): Observable<TypedAction<string> & { link: string }> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(reportOpenNewLinkDialog),
      tap((action: TypedAction<string> & { link: string }): void => {
        this._store.dispatch(dialogOpen({
          component: DialogLinkInfoComponent,
          config: {
            width: '500px',
            height: 'auto'
          },
          data: {
            link: action.link
          }
        }));
      })
    ))
  ));

  private readonly _saveDialog: () => void  = this.effect((params$: Observable<void>): Observable<[TypedAction<string> & { exit?: boolean, keepParent?: boolean }, ReportStoreState]> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(reportOpenSaveDialog),
      withLatestFrom(this.state$),
      tap(([action, state]: [TypedAction<string> & { exit?: boolean, keepParent?: boolean }, ReportStoreState]): void => {
        this._store.dispatch(dialogOpen({
          component: ReportSaveDialogComponent,
          keepParent: action?.keepParent || false,
          config: {
            width: '500px',
            height: 'auto'
          },
          data$: {
            report: this.report$,
            params: this.requestParamsSubmitted$,
            options: this.requestOptions$,
            reportType: this.reportType$,
            limit: this.limit$
          },
          data: {
            exit: action.exit || false,
          }
        }));
      })
    ))
  ));

  private readonly _shareDialog: () => void  = this.effect((params$: Observable<void>): Observable<TypedAction<string>> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(reportOpenSendDialog),
      tap((): void => {
        this._store.dispatch(dialogOpen({
          component: ReportSendDialogComponent,
          config: {
            width: '500px',
            height: 'auto'
          },
          data$: {
            report: this.report$,
            params: this.requestParamsSubmitted$,
            reportType: this.reportType$,
            name: this.name$
          }
        }));
      })
    ))
  ));

  private readonly _shareWithLinkDialog: () => void = this.effect((params$: Observable<void>): Observable<TypedAction<string>> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(reportOpenShareWithLinkDialog),
      tap((): void => {
        this._store.dispatch(dialogOpen({
          component: ReportSharePublicDialogComponent,
          config: {
            width: '500px',
            height: 'auto'
          },
          data$: {
            report: this.report$,
            params: this.requestParamsSubmitted$,
            reportType: this.reportType$
          }
        }));
      })
    ))
  ));

  private readonly _downloadDialog: () => void = this.effect((params$: Observable<void>): Observable<[TypedAction<string> & { format: string, separator: string }, ReportStoreState]> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(reportOpenDownloadDialog),
      withLatestFrom(this.state$),
      tap(([action, state]: [TypedAction<string> & { format: string, separator: string }, ReportStoreState]): void => {
        this._store.dispatch(reportDownload({
          params: state.requestParams,
          reportData: {
            name: state.name,
            reportType: state.reportType
          },
          format: action.format,
          separator: action.separator
        }));
      })
    ))
  ));

  private readonly _exitDialog: () => void = this.effect((params$: Observable<void>): Observable<TypedAction<string>> => params$.pipe(
    switchMap(() => this._actions$.pipe(
      ofType(reportOpenExitDialog),
      tap((): void => {
        this._store.dispatch(dialogOpen({
          component: DialogConfirmComponent,
          config: {
            width: '500px',
            height: 'auto'
          },
          data: {
            title: 'button.save',
            message: 'reports.exit_the_report_message',
            cancelText: 'button.no',
            submitText: 'button.yes',
            onSubmit: (): boolean => {
              this._store.dispatch(reportOpenSaveDialog({ exit: true }));
              return true;
            },
            onClose: (): void => {
              this.exit();
            }
          }
        }));
      })
    ))
  ));

  // #############################
  // EVENTS
  // #############################

  private readonly _reload$: Subscription = this.effect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(
      enableMetricsConsentAutoCorrection,
      disableMetricsConsentAutoCorrection,
      changeRealConversionsCorrectionModeSuccess
    ),
    tap((): void => this.patchState({ disabled: true }))
  ));

  private readonly _reloadSuccess$: Subscription = this.effect(() => this._actions$.pipe(
    ofType(
      enableMetricsConsentAutoCorrectionSuccess,
      disableMetricsConsentAutoCorrectionSuccess,
      changeRealConversionsCorrectionModeSuccess
    ),
    withLatestFrom(this.state$),
    tap(([action, state]: [TypedAction<string>, ReportStoreState]): void => {
      const options: Partial<AdRequestOptions> = {
        ...state.requestOptions
      };

      this.patchState({
        disabled: false,
        requestOptions: options
      });
    })
  ));

  private readonly available_report_params: { [key: string]: Array<string> } = {
    'dashboard': ['period'],
    'performance': ['dimensions', 'metrics', 'period'],
    'cycle': ['dimensions', 'conversion_metric', 'metrics', 'period'],
    'affinity': ['conversion_metric', 'channel', 'period'],
    'graph': ['dimensions', 'metrics', 'period'],
    'chart': ['dimensions', 'metrics', 'period'],
    'path-explorer': ['conversion_metric', 'period'],
    'transaction-id': ['conversion_metric', 'channel', 'period'],
    'attribution-overview': ['conversion_metric', 'channel', 'period']
  };

  // #############################
  // INIT PARAMS
  // #############################

  private readonly _initReportParams: () => void = this.effect((params$: Observable<void>): Observable<any> => params$.pipe(switchMap(() => combineLatest([
    this.requestParams$,
    this._store.select(selectBackgroundLoaded)
  ])
    .pipe(
      filter(([params, loaded]: [Partial<AdRequestParams> | undefined, boolean]): boolean => !!params && loaded),
      first(),
      tap((): void => this.patchState({ submitted: false })),
      withLatestFrom(this.state$),
      switchMap(([_, state]: [[Partial<AdRequestParams> | undefined, boolean], ReportStoreState]) => {
        const configs: Array<ParamConfig> = state.paramsConfigs.filter((config: ParamConfig): boolean => 'data' in config && config.data);
        const data: Array<Observable<any>> = configs.map((config: ParamConfig): Observable<any> => this._store.select(config.data));

        if (!data.length) {
          return of({});
        }

        return combineLatest(data).pipe(
          map((results: Array<any>) => {
            const output: object = {};

            results.forEach((result: any, index: number) => {
              output[configs[index].controlName] = result;
            });

            return output;
          })
        );
      }),
      withLatestFrom(
        this.state$,
        this._store.select(selectDimensionComponentObjects),
        this._store.select(selectMetricComponentObjects),
        this._store.select(selectChannelsNotRequired),
        this._store.select(selectPeriods)
      ),
      tap(([data, state, dimensions, metrics, channels, periods]: [
        object,
        ReportStoreState,
        DimensionComponentObjects,
        MetricComponentObjects,
        Serializers<Channel>,
        Array<AdRPeriod>
      ]): void => {
        let submitted: boolean = state.type !== null;

        const parameters: Partial<AdRequestParams> = {
          period: { type: 'last_30_days' },
          compare: { on: null, value: null },
          filters: {},
          ...state.requestParams
        };

        const reportParams: Partial<AdReportParams> = {
          compare: parameters.compare,
          filters: parameters.filters
        };

        if ('dimensions' in parameters && this.available_report_params[state.reportType].includes('dimensions')) {
          reportParams['dimensions'] = parameters.dimensions
            ?.map((slug: string): DimensionComponentObject | undefined => dimensions.find((dim: DimensionComponentObject): boolean => dim.payload.attributes.slug === slug))
            .filter((dimension: DimensionComponentObject | undefined): boolean => !!dimension);

          submitted = submitted && reportParams['dimensions'].length > 0;
        }

        if ('metrics' in parameters && this.available_report_params[state.reportType].includes('metrics')) {
          reportParams['metrics'] = parameters.metrics
            ?.map((slug: string): MetricComponentObject | undefined => metrics.find((met: MetricComponentObject): boolean => met.payload.attributes.slug === slug))
            .filter((metric: MetricComponentObject | undefined): boolean => !!metric);

          submitted = submitted && reportParams['metrics'].length > 0;
        }

        if ('conversion_metric' in parameters && this.available_report_params[state.reportType].includes('conversion_metric')) {
          reportParams['conversion_metric'] = metrics.find((metric: MetricComponentObject): boolean => metric.payload.attributes.slug === parameters.conversion_metric);

          if ([null, 'local'].includes(state.type) && !reportParams['conversion_metric'] && 'conversion_metric' in data) {
            reportParams['conversion_metric'] = data['conversion_metric'][0];

            if (reportParams['conversion_metric']) {
              parameters['conversion_metric'] = reportParams['conversion_metric'].payload.attributes.slug;
            }
          }

          submitted = submitted && reportParams['conversion_metric'] !== undefined;
        }

        if ('channel' in parameters && this.available_report_params[state.reportType].includes('channel')) {
          reportParams['channel'] = channels.find((channel: Serializer<Channel>): boolean => channel.attributes.slug === parameters.channel);

          submitted = submitted && reportParams['channel'] !== undefined;
        }

        if ('period' in parameters) {
          reportParams['period'] = periods.find((period: AdRPeriod): boolean => period.type === parameters.period.type);
          Object.assign(reportParams['period'], parameters.period);
        }

        if (['dashboard'].includes(state.reportType)) {
          submitted = true;
        }

        this.patchState({
          submitted: submitted,
          initialized: true,
          reportParams: reportParams,
          reportParamsSubmitted: reportParams,
          requestParams: parameters,
          requestParamsSubmitted: parameters,
          requestOptions: {}
        });
      })
  ))));

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<AppState>,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _reportsRequestsS: ReportsRequestsService,
    private readonly _translateS: TranslateService,
    private readonly _localStorageS: LocalStorageService,
    private readonly _periodsS: PeriodsService,
    private readonly _location: Location,
    private readonly _exitReportGuardService: ExitReportGuardService,
    private readonly _paramsConfigS: ParamsConfigsService,
    private readonly _reportParamsService: ReportParamsService,
    private readonly _savedReportService: SavedReportsService
  ) {
    super({
      uuid: null,
      token: null,
      type: null,
      report: null,
      template: null,
      name: null,
      requestParams: null,
      requestParamsSubmitted: null,
      reportParamsSubmitted: null,
      requestOptions: {
        real_conversions: false
      },
      reportParams: null,
      disabledParams: {},
      reportType: null,
      iframe: false,
      parameters: null,
      paramsConfigs: [],
      actionsConfigs: [],
      paramsDropdownConfigs: [],
      helpTemplate: null,
      periodFromUrl: null,
      filtersFromUrl: null,
      disabledValues: {},
      loading: false,
      initialized: false,
      disabled: false,
      submitted: false,
      authorize: true,
      owner: null,
      limit: null
    });
  }

  public init(
    parameters: any = {},
    iframe: boolean = null,
    paramsConfigs: Array<string> = [],
    actionsConfigs: Array<string> = [],
    paramsDropdownConfigs: Array<string> = [],
    helpTemplate: string = null,
    enableDialogs: boolean = false
  ): void {
    this.patchState({
      parameters,
      iframe,
      paramsConfigs: this._paramsConfigS.getConfig(paramsConfigs),
      actionsConfigs: this._paramsConfigS.getConfig(actionsConfigs),
      paramsDropdownConfigs: this._paramsConfigS.getConfig(paramsDropdownConfigs),
      helpTemplate
    });

    this._initReportParams();
    this._routeDataChanges();
    this._routeQueryChanges();
    this._routeParamsChanges();
    this._updateSite();
    this._createUpdateReportSuccess();
    this._createReportSuccess();
    this._createExportsSuccess();

    if (enableDialogs) {
      this._newLinkDialog();
      this._downloadDialog();
      this._shareWithLinkDialog();
      this._shareDialog();
      this._saveDialog();
      this._exitDialog();

      this._exitReportGuardService.canExitBehaviorSubject.next(true);
    }
  }

  public initLocalStorage(
    key: string,
    paramsConfigs: Array<string>,
    actionsConfigs: Array<string>,
    paramsDropdownConfigs: Array<string>,
  ): void {
    this.patchState({
      reportType: 'attribution-overview',
      paramsConfigs: this._paramsConfigS.getConfig(paramsConfigs),
      actionsConfigs: this._paramsConfigS.getConfig(actionsConfigs),
      paramsDropdownConfigs: this._paramsConfigS.getConfig(paramsDropdownConfigs)
    });

    this._initLocalStorage({
      key
    });
  }

  public saveLocalStorage(key: string): void {
    this._saveLocalStorage({ key: key });
  }

  public initPublicReport(report: Serializer<SavedReport>, uuid: string, token: string): void {
    this.patchState({
      report: report,
      uuid: uuid,
      token: token,
      name: report.attributes.name,
      reportType: report.attributes.report_type,
      requestParams: report.attributes.parameters,
      requestOptions: {
        real_conversions: report.attributes.real_conversions_is_enabled
      },
      type: 'public',
      loading: false,
      initialized: true
    });
  }

  // init params without params component
  public initParams(
    requestParams: Partial<AdRequestParams>,
    reportParams: Partial<AdReportParams>,
    requestOptions: Partial<AdRequestOptions>,
    limit: number | null,
    uuid: string | null,
    token: string | null,
    report: Serializer<SavedReport> | null
  ): void {
    this._initParams({
      initParams: requestParams,
      requestParams: requestParams,
      reportParams: reportParams,
      requestOptions: requestOptions,
      uuid: uuid,
      token: token,
      limit: limit,
      report: report
    });
  }

  // init | update params without load, must be used only with the report params component
  public updateRequestParamsSubmitted(params: Partial<AdRequestParams>): void {
    this._updateRequestParamsSubmitted(params);
  }

  public updateReportParams(params: Partial<AdReportParams>): void {
    this._updateReportParams(params);
  }

  // reload report, to be used by params component
  public updateReportParamsSubmitted(params: Partial<AdReportParams>): void {
    this._updateReportParamsSubmitted(params);
    this._exitReportGuardService.canExitBehaviorSubject.next(false);
  }

  public updateLimit(limit: number): void {
    this.patchState({ limit: limit });
  }

  public setDisabledValues(controlName: string, options: any): void {
    this._setDisabledValues({
      controlName,
      options
    });
  }

  private _getUrlParameters(report: Serializer<SavedReport | ReportTemplate> = null): Partial<AdRRequestParams> | null {
    if (this._route.snapshot.queryParams.params) {
      const params: Partial<AdRRequestParams> = JSON.parse(this._route.snapshot.queryParams.params);

      if (report) {
        report.attributes.parameters = {
          ...report.attributes.parameters,
          ...params
        };
      }

      return params;
    }

    return null;
  }

  // À supprimer prochainement et passer les paramètres via params
  private _getPeriodFromUrl(params: Partial<AdRRequestParams>): void {
    if (this._route.snapshot.queryParams['periodType']) {
      params.period = {
        type: this._route.snapshot.queryParams['periodType']
      };

      if (this._route.snapshot.queryParams['periodType'] === 'custom') {
        params.period.from = this._route.snapshot.queryParams['periodFrom'];
        params.period.to = this._route.snapshot.queryParams['periodTo'];
      }

      if (this._route.snapshot.queryParams['compareWithType']) {
        params.compare.value = {
          type: 'custom'
        };

        if (this._route.snapshot.queryParams['compareWithType'] === 'custom') {
          params.compare.value.from = this._route.snapshot.queryParams['compareWithFrom'];
          params.compare.value.to = this._route.snapshot.queryParams['compareWithTo'];
        } else if (this._route.snapshot.queryParams['compareWithType'] === 'previous_period' || this._route.snapshot.queryParams['compareWithType'] === 'previous_year') {
          const range: { from: string, to: string } = this._periodsS.getDateRange(this._route.snapshot.queryParams['compareWithType'], this._route.snapshot.queryParams['periodFrom'], this._route.snapshot.queryParams['periodTo']);

          params.compare.value.compareWithType = this._route.snapshot.queryParams['compareWithType'];
          params.compare.value.from = range.from;
          params.compare.value.to = range.to;
        }
      }
    }
  }

  public exit(): void {
    this._exitReportGuardService.exitReport();
  }
}
